import { createSlice } from '@reduxjs/toolkit';
import { loadState } from 'common/helpers/localStorage';

export const environmentOrgKey = (product, environment) =>
  `${product}:${environment}`;

const anyEnvironmentLoading = (environmentStatus) =>
  Object.values(environmentStatus).some((status) => status === 'loading');

export const initialState = {
    isSaving: false,
    // Single-org polling after a create; unrelated to the environment org lists.
    isFetchingOrganization: false,
    isFinishedCreating: true,
    environmentOrganizations: loadState('organizations.environmentOrganizations') ?? {},
    // Per `${product}:${environment}`: 'idle' | 'loading' | 'succeeded' | 'failed'.
    // Only `environmentOrganizations` is rehydrated from localStorage, so a reload
    // starts every key back at 'idle' and a previously failed fetch is retried.
    environmentStatus: {},
    environmentErrors: {},
    organizations: {},
    // Reserved for the save / polling flows only, so a failed environment list
    // never raises a banner on an unrelated screen.
    error: null,
    succeeded: false,
    creatingSubdomain: null,
};

export const slice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    fetchOrganizationRequested: (state) => {
      state.isFetchingOrganization = true;
      state.error = null;
    },
    fetchOrganizationFailed: (state, action) => {
      state.isFetchingOrganization = false;
      state.error = action.payload;
      state.isFinishedCreating = true;
    },
    fetchOrganizationSucceeded: (state, action) => {
      state.isFetchingOrganization = false;
      state.error = null;
      state.isFinishedCreating = true;
      const { payload: item } = action;
      state.organizations[item.subdomain] = item;
    },
    fetchOrganizationsForEnvironmentRequested: (state, action) => {
      const { product, environment } = action.payload;
      const key = environmentOrgKey(product, environment);
      state.environmentStatus[key] = 'loading';
      delete state.environmentErrors[key];
    },
    fetchOrganizationsForEnvironmentSucceeded: (state, action) => {
      const { product, environment, organizations } = action.payload;
      const key = environmentOrgKey(product, environment);
      state.environmentStatus[key] = 'succeeded';
      delete state.environmentErrors[key];
      if (!state.environmentOrganizations.hasOwnProperty(product)) {
        state.environmentOrganizations[product] = {};
      }
      state.environmentOrganizations[product][environment] = organizations;
    },
    fetchOrganizationsForEnvironmentFailed: (state, action) => {
      const { product, environment, error } = action.payload;
      const key = environmentOrgKey(product, environment);
      state.environmentStatus[key] = 'failed';
      state.environmentErrors[key] = error;
    },
    saveOrganizationRequested: (state, action) => {
      state.isSaving = true;
      state.error = null;
      state.succeeded = false;
      state.creatingSubdomain = {
        'subdomain': action.payload.subdomain,
        'environment': action.payload.environment
      };
    },
    saveOrganizationFailed: (state, action) => {
      state.isSaving = false;
      state.error = action.payload;
      state.succeeded = false;
      state.isFinishedCreating = true;      
    },
    saveOrganizationSucceeded: (state, action) => {
      state.isSaving = false;
      state.error = null;
      state.succeeded = true;
      state.isFinishedCreating = false;
      const item = action.payload;
      state.organizations[item.subdomain] = item;
    },
    resetSaveState: (state) => {
      state.error = null;
      state.isSaving = false;
      state.succeeded = false;
    },
    resetData: (state) => {
      state.environmentOrganizations = {};
      state.environmentStatus = {};
      state.environmentErrors = {};
    }
  }
});

// Actions
export const {
  fetchOrganizationRequested,
  fetchOrganizationFailed,
  fetchOrganizationSucceeded,
  fetchOrganizationsForEnvironmentRequested,
  fetchOrganizationsForEnvironmentSucceeded,
  fetchOrganizationsForEnvironmentFailed,
  saveOrganizationRequested,
  saveOrganizationFailed,
  saveOrganizationSucceeded,
  resetSaveState,
  resetData
} = slice.actions;

// Selectors
export const selectIsSaving = (state) => state.organizations.isSaving;
export const selectSucceeded = (state) => state.organizations.succeeded;
export const selectError = (state) => state.organizations.error;
export const selectOrganizations = (state) => state.organizations.organizations;
export const selectIsFinishedCreating = (state) => state.organizations.isFinishedCreating;
export const selectCreatingSubdomain = (state) => state.organizations.creatingSubdomain;
export const selectEnvironmentOrganizations = (state) => state.organizations.environmentOrganizations;

export const selectIsFetching = (state) =>
  state.organizations.isFetchingOrganization ||
  anyEnvironmentLoading(state.organizations.environmentStatus);

export const selectIsLoaded = (state) =>
  Object.keys(state.organizations.environmentOrganizations).length > 0 &&
  !anyEnvironmentLoading(state.organizations.environmentStatus);

export const selectEnvironmentOrganizationsStatus = (state, product, environment) => {
  if (!product || !environment) {
    return 'idle';
  }
  return (
    state.organizations.environmentStatus[
      environmentOrgKey(product, environment)
    ] ?? 'idle'
  );
};

export const selectEnvironmentOrganizationsError = (state, product, environment) => {
  if (!product || !environment) {
    return null;
  }
  return (
    state.organizations.environmentErrors[
      environmentOrgKey(product, environment)
    ] ?? null
  );
};

export default slice.reducer;
